import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { SEO_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { serviceCategories, professionalProfiles, professionalCategories, geoFrance } from '@afficheservices/db/schema'
import { eq, and, sql, desc, isNull } from 'drizzle-orm'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'
import { seoMeta } from '~/lib/seo-meta'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { parseServiceCitySlug, generatePageContent, SERVICE_LABELS, SERVICE_PRICES } from '~/lib/programmatic-seo'
import { InlineQuiz } from '~/components/inline-quiz'
import { getSeoContent } from '~/lib/seo-content.server'
import { formatRating } from '~/lib/format'
import { LeadCaptureBlock, SeoExitIntent } from '~/components/lead-capture-block'
import { GeoAnswerBlock, SiloLinks } from '~/components/geo-answer'
import { AuthorByline, AuthorSchema, getAuthorForCategory } from '~/components/eeat-author'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { ArticleSchema } from '~/components/seo-content'
import { ContentMeta, SourceCitations, InternaunteQuestions } from '~/components/content-trust'
import { CompactServicePage } from '~/components/compact-service-page'
import { fetchCityServiceStats } from '~/lib/city-service-stats.server'
import {
  Star, CheckCircle, MapPin, ArrowRight, Phone,
  Shield, Clock, Users, AlertTriangle, Zap, BadgeCheck,
  PhoneCall, Calendar, Wrench,
} from 'lucide-react'

// ─── Emergency-specific content generator ────────────────────

function generateEmergencyContent(serviceSlug: string, cityName: string) {
  const svc = SERVICE_LABELS[serviceSlug] ?? { name: serviceSlug, artisan: serviceSlug, article: 'un' }
  const price = SERVICE_PRICES[serviceSlug]

  const emergencyScenarios: Record<string, string[]> = {
    plomberie: [
      'Fuite d\'eau importante ou dégât des eaux',
      'Canalisation bouchée ou refoulement d\'eaux usées',
      'Chauffe-eau en panne (plus d\'eau chaude)',
      'Rupture de tuyau ou de joint',
      'WC bouché ou débordement',
      'Fuite de gaz au niveau du chauffe-eau',
    ],
    electricite: [
      'Panne de courant totale dans le logement',
      'Court-circuit ou odeur de brûlé',
      'Disjoncteur qui saute en boucle',
      'Fils électriques dénudés ou exposés',
      'Prise ou interrupteur qui chauffe anormalement',
      'Panne du tableau électrique',
    ],
    serrurerie: [
      'Porte claquée — clés restées à l\'intérieur',
      'Serrure cassée ou bloquée',
      'Tentative de cambriolage — porte endommagée',
      'Perte de clés — besoin d\'un double',
      'Cylindre de serrure arraché',
      'Volet roulant bloqué',
    ],
    toiture: [
      'Fuite de toiture après intempéries',
      'Tuiles arrachées par le vent',
      'Infiltration d\'eau dans les combles',
      'Gouttière arrachée ou bouchée',
      'Dégât de grêle sur la toiture',
      'Charpente endommagée suite à une tempête',
    ],
    climatisation: [
      'Panne de climatisation en pleine canicule',
      'Panne de chauffage en hiver',
      'Fuite de fluide frigorigène',
      'Climatiseur qui fait un bruit anormal',
      'Mauvaises odeurs provenant du climatiseur',
      'Pompe à chaleur en panne',
    ],
  }

  const scenarios = emergencyScenarios[serviceSlug] ?? [
    `Urgence ${svc.name.toLowerCase()} nécessitant une intervention rapide`,
    `Panne ou dysfonctionnement nécessitant un dépannage`,
    `Situation d'urgence liée à ${svc.name.toLowerCase()}`,
    `Intervention de nuit ou le week-end`,
  ]

  const waitingTips: Record<string, string[]> = {
    plomberie: [
      'Coupez l\'arrivée d\'eau générale (vanne sous l\'évier ou au compteur)',
      'Placez des serpillières et bassines pour limiter les dégâts',
      'Si dégât des eaux : coupez l\'électricité dans la zone touchée',
      'Prenez des photos pour votre assurance',
      'Ne touchez pas aux canalisations si vous sentez du gaz',
    ],
    electricite: [
      'Ne touchez aucun appareil ou fil électrique',
      'Coupez le disjoncteur général si possible',
      'Éloignez les enfants et animaux de la zone à risque',
      'N\'utilisez pas d\'eau à proximité des installations électriques',
      'Aérez si vous sentez une odeur de brûlé',
    ],
    serrurerie: [
      'Ne tentez pas de forcer la serrure vous-même',
      'Vérifiez si une fenêtre est accessible en toute sécurité',
      'Restez à proximité de votre domicile',
      'Préparez une pièce d\'identité pour le serrurier',
      'Contactez votre assurance habitation (souvent couverte)',
    ],
  }

  const tips = waitingTips[serviceSlug] ?? [
    'Mettez en sécurité les personnes présentes',
    'Prenez des photos de la situation pour l\'assurance',
    'Notez les détails du problème pour le professionnel',
    'Préparez l\'accès au logement pour l\'artisan',
  ]

  const faqs = [
    {
      question: `Quel est le délai d'intervention pour ${svc.article} ${svc.artisan} en urgence à ${cityName} ?`,
      answer: `En urgence, nos ${svc.artisan}s partenaires à ${cityName} interviennent généralement sous 30 minutes à 2 heures, selon la disponibilité et votre localisation exacte. Les interventions sont possibles 7j/7, y compris le soir et le week-end.`,
    },
    {
      question: `Combien coûte une intervention urgente de ${svc.artisan} à ${cityName} ?`,
      answer: price
        ? `Une intervention urgente coûte en moyenne ${Math.round(price.min * 1.3)}–${Math.round(price.max * 1.5)} € à ${cityName}. Un supplément de 30 à 50% s'applique généralement pour les interventions de nuit (après 20h), le week-end et les jours fériés.`
        : `Le tarif dépend de l'intervention. Un devis est toujours fourni avant travaux. Comptez un supplément pour les interventions de nuit et le week-end.`,
    },
    {
      question: 'L\'intervention urgente est-elle garantie ?',
      answer: 'Oui, tous nos artisans partenaires sont assurés en responsabilité civile professionnelle (RC Pro). Les travaux sont garantis et un devis est systématiquement remis avant intervention.',
    },
    {
      question: `Puis-je faire intervenir ${svc.article} ${svc.artisan} la nuit ou le dimanche à ${cityName} ?`,
      answer: `Oui, nos partenaires proposent des interventions de nuit et le week-end à ${cityName}. Un supplément horaire s'applique (généralement +30 à 50% du tarif normal). Demandez un rappel immédiat via notre formulaire pour une mise en relation directe.`,
    },
    {
      question: 'Mon assurance couvre-t-elle l\'intervention d\'urgence ?',
      answer: 'Dans la plupart des cas, oui. L\'assurance habitation (garantie dégât des eaux, bris de glace, etc.) couvre souvent les frais de dépannage d\'urgence. Conservez la facture et les photos pour votre dossier.',
    },
  ]

  return { scenarios, tips, faqs }
}

// ─── Loader ──────────────────────────────────────────────────

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params
  const parsed = parseServiceCitySlug(slug ?? '')

  if (!parsed) throw new Response('Page non trouvée', { status: 404 })

  const { serviceSlug, cityName } = parsed
  await assertCityNotDomTom(cityName)
  const content = generatePageContent('urgence', serviceSlug, cityName)
  const svcInfo = SERVICE_LABELS[serviceSlug]
  const price = SERVICE_PRICES[serviceSlug]
  const emergency = generateEmergencyContent(serviceSlug, cityName)

  // Find pros
  const [cat] = await db
    .select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(eq(serviceCategories.slug, serviceSlug))
    .limit(1)

  let pros: any[] = []
  if (cat) {
    pros = await db
      .select({
        companyName: professionalProfiles.companyName,
        slug: professionalProfiles.slug,
        city: professionalProfiles.city,
        ratingAvg: professionalProfiles.ratingAvg,
        reviewCount: professionalProfiles.reviewCount,
        identityVerified: professionalProfiles.identityVerified,
      })
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(
        eq(professionalCategories.categoryId, cat.id),
        eq(professionalProfiles.identityVerified, true),
      ))
      .orderBy(desc(professionalProfiles.ratingAvg))
      .limit(6)
  }

  // Related cities for internal links
  const relatedCities = await db
    .select({ name: geoFrance.communeName })
    .from(geoFrance)
    .where(sql`${geoFrance.communeName} != ${cityName}`)
    .orderBy(sql`RANDOM()`)
    .limit(8)

  // Related emergency services
  const relatedServices = await db
    .select({ name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(and(isNull(serviceCategories.parentId), sql`${serviceCategories.slug} != ${serviceSlug}`))
    .orderBy(sql`RANDOM()`)
    .limit(5)

  // Proprietary data moat — live stats per (service, city)
  const stats = cat
    ? await fetchCityServiceStats(cat.id, cityName)
    : { recentLeads: 0, avgResponseHours: null, proCount: 0, priceBand: null, avgRating: null, reviewCount: 0 }

  return {
    content,
    serviceSlug,
    cityName,
    slug: slug ?? '',
    svcInfo,
    price,
    pros,
    relatedCities: relatedCities.map((c) => c.name),
    relatedServices,
    emergency,
    aiContent: await getSeoContent(serviceSlug, cityName),
    stats,
  }
}

export function headers() { return SEO_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  // Noindex when no DB-backed AI content AND no live urgency demand signal
  // (GSC "Crawled - not indexed" report 2026-04: ~200 urgence URLs flagged).
  const hasSignal = !!data?.aiContent || (data?.stats && data.stats.recentLeads > 5)
  return seoMeta({
    title: data?.content?.title ?? 'Urgence travaux',
    description: data?.content?.description ?? '',
    path: `/urgence/${data?.slug ?? ''}`,
    imageTitle: data?.content?.h1 ?? data?.content?.title,
    imageSubtitle: 'Intervention urgente · Pros vérifiés 7j/7',
    noindex: !hasSignal,
  })
}

// ─── Component ───────────────────────────────────────────────

export default function UrgencePage() {
  const { content, serviceSlug, cityName, slug, svcInfo, relatedCities, relatedServices, emergency, aiContent, stats } = useLoaderData<typeof loader>()

  const artisanName = svcInfo?.artisan ?? serviceSlug
  const citySlug = cityName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const artisanSlug = artisanName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <FAQSchema items={aiContent?.faqItems ? aiContent.faqItems.map(f => ({ question: f.q, answer: f.a })) : emergency.faqs} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Urgences', url: 'https://www.afficheservices.fr/urgence' },
        { name: content.h1 ?? `Urgence ${svcInfo?.name ?? ''} à ${cityName}`, url: `https://www.afficheservices.fr/urgence/${slug}` },
      ]} />
      <ArticleSchema title={content.title ?? ''} description={content.description ?? ''} url={`https://www.afficheservices.fr/urgence/${slug}`} />
      <AuthorSchema author={getAuthorForCategory(serviceSlug)} articleUrl={`https://www.afficheservices.fr/urgence/${slug}`} />

      <CompactServicePage
        artisanName={artisanName}
        serviceName={svcInfo?.name ?? serviceSlug}
        cityName={cityName}
        citySlug={citySlug}
        serviceSlug={serviceSlug}
        stats={stats}
        faqs={aiContent?.faqItems ?? emergency.faqs.map((f) => ({ q: f.question, a: f.answer }))}
        expertIntroParas={aiContent?.expertIntroParas ?? []}
        relatedServices={relatedServices.map((s) => ({ slug: s.slug, name: s.name, artisan: s.slug }))}
        nearbyCities={relatedCities}
        variant="urgency"
        emergencyScenarios={emergency.scenarios}
        waitingTips={emergency.tips}
      />

      {/* Inline emergency call-to-action — primary tel: for panic-intent users.
          Placed below the H1 hero, no SEO impact, additive only. */}
      <div className="mx-auto max-w-4xl px-4">
        <div className="mt-6 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 p-6 text-center text-white shadow-lg">
          <p className="text-xs font-semibold uppercase tracking-wider text-red-200">Urgence {artisanName} {cityName}</p>
          <h2 className="mt-1 text-lg font-bold">Appelez maintenant — intervention 7j/7</h2>
          <div className="mt-4 flex flex-col items-center gap-3">
            <a
              href="tel:+33743019662"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-base font-bold text-red-700 shadow-md hover:bg-slate-50"
            >
              <PhoneCall className="h-5 w-5" /> Appeler — 07 43 01 96 62
            </a>
            <Link
              to="/demande-devis/1"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 underline-offset-4 hover:underline"
            >
              Ou demander un rappel sous 30 min
            </Link>
          </div>
        </div>
      </div>

      {/* Note: the global StickyMobileCta (rendered in root.tsx via
          DeferredOverlays) already exposes a tel: + devis dual-button bar
          on mobile after scroll. A second urgence-only sticky bar at
          bottom-0 collided visually with it (a11y review flagged HIGH).
          Removed; the inline red CTA above gives the panic-intent user
          a tel: link in the hero area. */}

      <SeoExitIntent serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} />
      <Footer contextTitle={`Urgences ${svcInfo?.name?.toLowerCase() ?? ''} — Autres villes`} contextLinks={
        relatedCities.slice(0, 6).map((city) => {
          const cs = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
          return { href: `/urgence/${artisanSlug}-${cs}`, label: `Urgence ${artisanName} ${city}` }
        })
      } />
    </div>
  )
}

// Old heavy template kept for reference but unreachable after compact refactor
function _UrgencePageLegacy() {
  const { content, serviceSlug, cityName, slug, svcInfo, price, pros, relatedCities, relatedServices, emergency, aiContent } = useLoaderData<typeof loader>()

  const artisanName = svcInfo?.artisan ?? serviceSlug

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <FAQSchema items={aiContent?.faqItems ? aiContent.faqItems.map(f => ({ question: f.q, answer: f.a })) : emergency.faqs} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Urgences', url: 'https://www.afficheservices.fr/urgence' },
        { name: content.h1, url: `https://www.afficheservices.fr/urgence/${slug}` },
      ]} />
      <ArticleSchema title={content.title ?? ''} description={content.description ?? ''} url={`https://www.afficheservices.fr/urgence/${slug}`} />
      <AuthorSchema author={getAuthorForCategory(serviceSlug)} articleUrl={`https://www.afficheservices.fr/urgence/${slug}`} />

      <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        <AuthorByline author={getAuthorForCategory(serviceSlug)} readTime={4} />
        <div className="mt-4">
          <GeoAnswerBlock
            question={`Urgence ${svcInfo?.name?.toLowerCase() ?? 'artisan'} à ${cityName} — qui appeler ?`}
            answer={`Pour une urgence ${svcInfo?.name?.toLowerCase() ?? ''} à ${cityName}, demandez un rappel immédiat via notre formulaire en ligne. Nos artisans d'urgence interviennent 7j/7, 24h/24 à ${cityName} et environs.`}
            details={[
              'Intervention d\'urgence 7j/7, 24h/24',
              `Artisans disponibles à ${cityName}`,
              'Devis avant intervention',
              'Rappel gratuit sous 30 min via formulaire',
            ]}
            ctaUrl={`/devis-gratuit?service=${serviceSlug}`}
            ctaLabel="Demander une intervention urgente"
          />
        </div>

        {/* ── Emergency Hero ── */}
        <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-red-600 to-orange-500 p-8 text-white shadow-lg">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-100">
            <AlertTriangle className="h-4 w-4" />
            Intervention urgente 7j/7
          </div>
          <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{content.h1}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-red-100">{content.intro}</p>

          {/* Quick action buttons — phone temporarily disabled, all CTAs to devis */}
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/demande-devis/1"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold text-red-600 shadow-md transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <PhoneCall className="h-4 w-4" /> Demander un rappel immédiat
            </Link>
          </div>

          {/* Trust badges */}
          <div className="mt-6 flex flex-wrap gap-4 border-t border-white/20 pt-4">
            <div className="flex items-center gap-1.5 text-xs text-red-100">
              <Clock className="h-3.5 w-3.5" /> Intervention sous 1h
            </div>
            <div className="flex items-center gap-1.5 text-xs text-red-100">
              <Calendar className="h-3.5 w-3.5" /> 7j/7 — soirs et week-ends
            </div>
            <div className="flex items-center gap-1.5 text-xs text-red-100">
              <Shield className="h-3.5 w-3.5" /> Artisans vérifiés et assurés
            </div>
            <div className="flex items-center gap-1.5 text-xs text-red-100">
              <BadgeCheck className="h-3.5 w-3.5" /> Devis avant intervention
            </div>
          </div>
        </div>

        {/* ── Callback banner (phone temporarily disabled) ── */}
        <div className="mt-4 rounded-xl border-2 border-red-200 bg-red-50 p-4">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <Phone className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-red-500">Urgence {artisanName}</p>
                <Link to="/demande-devis/1" className="text-xl font-bold text-red-700 hover:text-red-800">Rappel sous 30 min</Link>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-xs text-red-600">Demande gratuite · Mise en relation immédiate</p>
              <p className="text-[10px] text-red-400">Disponible 7j/7, 24h/24</p>
            </div>
          </div>
        </div>

        {/* ── When to call ── */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Quand appeler un {artisanName} en urgence à {cityName} ?
          </h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {emergency.scenarios.map((scenario, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg bg-orange-50 p-3">
                <Zap className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                <span className="text-sm text-slate-700">{scenario}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── How it works ── */}
        <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Comment ça marche ?</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {[
              { step: 1, icon: PhoneCall, title: 'Appelez-nous', desc: `Décrivez votre urgence ${svcInfo?.name.toLowerCase() ?? ''} par téléphone ou via le formulaire.`, color: 'bg-red-50 text-red-600' },
              { step: 2, icon: Users, title: 'Mise en relation', desc: `Un ${artisanName} disponible à ${cityName} est identifié et vous est envoyé.`, color: 'bg-orange-50 text-orange-600' },
              { step: 3, icon: Wrench, title: 'Intervention rapide', desc: 'L\'artisan arrive, établit un devis sur place et intervient immédiatement après accord.', color: 'bg-emerald-50 text-emerald-600' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl ${s.color}`}>
                  <s.icon className="h-6 w-6" />
                </div>
                <p className="mt-3 text-xs font-bold uppercase tracking-wider text-slate-400">Étape {s.step}</p>
                <h3 className="mt-1 text-sm font-bold text-slate-900">{s.title}</h3>
                <p className="mt-1 text-xs text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Waiting tips ── */}
        <div className="mt-6 rounded-2xl border-2 border-amber-200 bg-amber-50 p-6">
          <h2 className="flex items-center gap-2 text-base font-bold text-amber-900">
            <Shield className="h-5 w-5 text-amber-600" />
            En attendant l'artisan — gestes de sécurité
          </h2>
          <ul className="mt-4 space-y-2">
            {emergency.tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* AI expert content */}
        {aiContent?.expertIntroParas && (
          <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <ContentMeta cityName={cityName} serviceName={svcInfo?.name ?? 'Travaux'} />
            <h2 className="mt-3 text-lg font-bold text-slate-900">Urgence {svcInfo?.name.toLowerCase()} à {cityName} — ce qu'il faut savoir</h2>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
              {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <SourceCitations serviceSlug={serviceSlug} />
          </article>
        )}

        {aiContent?.localContextParas && (
          <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-6">
            <h3 className="text-base font-bold text-blue-900">Spécificités locales — {cityName}</h3>
            <div className="mt-3 space-y-2 text-sm leading-relaxed text-blue-800">
              {aiContent.localContextParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        )}

        {aiContent?.priceAnalysis && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <h3 className="text-base font-bold text-emerald-900">Analyse des tarifs d'urgence — {cityName}</h3>
            <p className="mt-3 text-sm leading-relaxed text-emerald-800">{aiContent.priceAnalysis}</p>
          </div>
        )}

        {/* ── Pricing info ── */}
        {price && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">Tarifs urgence {svcInfo?.name.toLowerCase()} à {cityName}</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="rounded-xl bg-slate-50 p-4 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Jour (8h-20h)</p>
                <p className="mt-1 text-lg font-bold text-slate-900">{price.min}–{price.max} €</p>
              </div>
              <div className="rounded-xl bg-orange-50 p-4 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-500">Soirée (20h-22h)</p>
                <p className="mt-1 text-lg font-bold text-orange-700">{Math.round(price.min * 1.3)}–{Math.round(price.max * 1.3)} €</p>
              </div>
              <div className="rounded-xl bg-red-50 p-4 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-red-500">Nuit (22h-8h)</p>
                <p className="mt-1 text-lg font-bold text-red-700">{Math.round(price.min * 1.5)}–{Math.round(price.max * 1.5)} €</p>
              </div>
              <div className="rounded-xl bg-purple-50 p-4 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-purple-500">Dimanche/Férie</p>
                <p className="mt-1 text-lg font-bold text-purple-700">{Math.round(price.min * 1.5)}–{Math.round(price.max * 1.5)} €</p>
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-400">* Tarifs indicatifs TTC. Devis exact fourni avant toute intervention.</p>
          </div>
        )}

        {/* ── Trust section ── */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Pourquoi nous faire confiance à {cityName} ?</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: BadgeCheck, title: 'Artisans vérifiés', desc: 'SIRET valide, assurance RC Pro, identité contrôlée.' },
              { icon: Clock, title: 'Intervention rapide', desc: 'Nos partenaires interviennent généralement sous 1 à 2 heures.' },
              { icon: Shield, title: 'Devis avant travaux', desc: 'Un devis détaillé est toujours remis et accepté avant le début de l\'intervention.' },
              { icon: Star, title: 'Avis vérifiés', desc: 'Consultez les avis clients avant de choisir votre artisan.' },
              { icon: MapPin, title: `Artisans locaux à ${cityName}`, desc: 'Des professionnels qui connaissent votre quartier et interviennent vite.' },
              { icon: Phone, title: 'Support 7j/7', desc: 'Notre équipe est joignable pour vous accompagner du début à la fin.' },
            ].map((p) => (
              <div key={p.title} className="rounded-xl bg-slate-50 p-4">
                <div className="flex items-center gap-2">
                  <p.icon className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-semibold text-slate-900">{p.title}</h4>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Pros in this city ── */}
        {pros.length > 0 && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">{svcInfo?.name ?? 'Artisans'} urgentistes à {cityName}</h2>
            <div className="mt-4 space-y-3">
              {pros.map((pro) => (
                <Link key={pro.slug} to={`/pro/${pro.slug}`}
                  className="flex items-center gap-3 rounded-xl bg-slate-50 p-3 transition-colors hover:bg-primary-50">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 text-sm font-bold text-red-600">
                    {pro.companyName[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{pro.companyName}</span>
                      {pro.identityVerified && <BadgeCheck className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {formatRating(pro.ratingAvg)} ({pro.reviewCount})
                      <span>·</span> {pro.city}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Questions des internautes — UGC-style */}
        {aiContent && (
          <InternaunteQuestions serviceSlug={serviceSlug} cityName={cityName} artisan={svcInfo?.artisan ?? 'artisan'} />
        )}

        {/* ── FAQ — AI or template ── */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes — Urgence {svcInfo?.name.toLowerCase()} à {cityName}</h2>
          <div className="mt-4 space-y-4">
            {aiContent?.faqItems ? (
              aiContent.faqItems.map((faq, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              ))
            ) : (
              emergency.faqs.map((faq, i) => (
                <details key={i} className="group border-b border-border pb-4 last:border-0">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-800 hover:text-primary">
                    {faq.question}
                    <span className="text-slate-300 transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.answer}</p>
                </details>
              ))
            )}
          </div>
        </div>

        {/* ── Internal links ── */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-white p-4 shadow-xs">
            <h3 className="text-xs font-semibold text-slate-500">Urgence {artisanName} dans d'autres villes</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {relatedCities.map((city) => {
                const artisanSlug = (svcInfo?.artisan ?? serviceSlug).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                const citySlug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                return (
                  <Link key={city} to={`/urgence/${artisanSlug}-${citySlug}`}
                    className="rounded-full border border-border px-2.5 py-1 text-[11px] text-slate-600 hover:border-red-400 hover:text-red-600">
                    Urgence {artisanName} {city}
                  </Link>
                )
              })}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-white p-4 shadow-xs">
            <h3 className="text-xs font-semibold text-slate-500">Autres urgences à {cityName}</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {relatedServices.map((rs) => {
                const rsLabel = SERVICE_LABELS[rs.slug]
                const artisanSlug = (rsLabel?.artisan ?? rs.slug).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                const citySlug = cityName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
                return (
                  <Link key={rs.slug} to={`/urgence/${artisanSlug}-${citySlug}`}
                    className="rounded-full border border-border px-2.5 py-1 text-[11px] text-slate-600 hover:border-red-400 hover:text-red-600">
                    Urgence {rsLabel?.artisan ?? rs.name} {cityName}
                  </Link>
                )
              })}
            </div>
          </div>
        </div>

        {/* ── Quiz ── */}
        <div className="mt-8">
          <InlineQuiz serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} />
        </div>

        {/* ── Final CTA ── */}
        <div className="mt-8 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 p-8 text-center text-white shadow-lg">
          <h2 className="text-xl font-bold">{content.cta}</h2>
          <p className="mt-2 text-sm text-red-200">Artisans vérifiés · Intervention rapide · Devis gratuit</p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/demande-devis/1"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-sm font-bold text-red-600 shadow-md hover:bg-slate-50"
            >
              <PhoneCall className="h-4 w-4" /> Rappel sous 30 min
            </Link>
          </div>
        </div>
      </div>

      {/* ── Sticky mobile CTA ── */}
      <div className="fixed bottom-16 left-0 right-0 z-40 border-t border-red-200 bg-red-600 p-2 sm:hidden">
        <Link
          to="/demande-devis/1"
          className="flex items-center justify-center gap-2 rounded-xl bg-white py-2.5 text-sm font-bold text-red-600"
        >
          <PhoneCall className="h-4 w-4" /> Rappel sous 30 min — Urgence {artisanName}
        </Link>
      </div>

      {/* Lead capture */}
      <div className="mx-auto max-w-4xl px-4 pb-8">
        <LeadCaptureBlock serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} variant="urgent" />
      </div>

      <div className="mx-auto max-w-4xl px-4 pb-8">
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <SiloLinks title="Pages liées" links={[
            { href: `/prix/${slug}`, label: `Prix ${artisanName} ${cityName}` },
          ]} />
          <SiloLinks title="Autres villes" links={
            relatedCities.slice(0, 5).map((city) => {
              const artisanSlug = (svcInfo?.artisan ?? serviceSlug).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              const citySlug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              return { href: `/urgence/${artisanSlug}-${citySlug}`, label: `Urgence ${artisanName} ${city}` }
            })
          } />
        </div>
      </div>

      <SeoExitIntent serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} />
      <Footer contextTitle={`Urgences ${svcInfo?.name.toLowerCase()} — Autres villes`} contextLinks={
        relatedCities.slice(0, 6).map((city) => {
          const artisanSlug = (svcInfo?.artisan ?? serviceSlug).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
          const citySlug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
          return { href: `/urgence/${artisanSlug}-${citySlug}`, label: `Urgence ${artisanName} ${city}` }
        })
      } />
    </div>
  )
}
